import React, { useState, useEffect, useCallback } from 'react';
import { PersonDetailData } from '../index';
import { Tag, Plus, Edit2, Trash2, Calendar, UserCheck, Radio } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { fetchWithAuth } from '../../../services/api';
import { TagFormModal, PersonTag } from '../../OperationalView/components/TagFormModal';
import { tagTypeLabel } from '../../../utils/terminology';
import toast from 'react-hot-toast';
import { useConfirm } from '../../ui/ConfirmDialog';

interface TagsTabProps {
  person: PersonDetailData;
  onRefresh: () => void;
  darkMode?: boolean;
}

export const TagsTab: React.FC<TagsTabProps> = ({ person, onRefresh, darkMode = false }) => {
  const confirm = useConfirm();
  const [tags, setTags] = useState<PersonTag[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTag, setEditingTag] = useState<PersonTag | null>(null);

  const customerId = person.customer?.id;
  const customerType = (person.customer?.type as 'condominio' | 'empresa') || 'condominio';

  const roles = person.roles || [];
  const isResident = roles.some(r => r.role_type === 'resident');
  const isStaff = roles.some(r => r.role_type === 'staff');
  const ownerType = isStaff ? 'employee' : 'resident';
  const canManage = !!customerId;

  const fetchTags = useCallback(async () => {
    if (!customerId) return;
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `/api/v1/customers/${customerId}/person-tags?person_id=${person.id}`
      );
      if (response.ok) {
        const data = await response.json();
        setTags(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching tags:', err);
    } finally {
      setIsLoading(false);
    }
  }, [customerId, person.id]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  const handleDelete = async (tag: PersonTag) => {
    if (!await confirm({ message: `Deseja realmente excluir a tag "${tag.tag_value}"?`, variant: 'danger', confirmLabel: 'Excluir' })) return;
    try {
      const response = await fetchWithAuth(
        `/api/v1/customers/${customerId}/person-tags/${tag.id}`,
        { method: 'DELETE' }
      );
      if (response.ok) {
        toast.success('Tag excluída');
        fetchTags();
      } else {
        const err = await response.json();
        toast.error(err.error || 'Erro ao excluir tag');
      }
    } catch (err) {
      toast.error('Erro ao excluir tag');
    }
  };

  const addButton = canManage && (
    <button
      onClick={() => setShowAddModal(true)}
      className={cn(
        'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
        'bg-orange-600 hover:bg-orange-500 text-white'
      )}
    >
      <Plus className="w-4 h-4" />
      Adicionar Tag
    </button>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  return (
    <>
      {tags.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 px-4">
          <div className={cn(
            'w-20 h-20 rounded-full flex items-center justify-center mb-4',
            darkMode ? 'bg-gray-800' : 'bg-gray-100'
          )}>
            <Tag className={cn('w-10 h-10', darkMode ? 'text-gray-500' : 'text-gray-400')} />
          </div>
          <p className={cn('text-center max-w-md mb-4', darkMode ? 'text-gray-400' : 'text-gray-600')}>
            Esta pessoa ainda não possui tags de acesso registradas.
          </p>
          {addButton}
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex items-center justify-end">
            {addButton}
          </div>

          <div className="space-y-3">
            {tags.map((tag) => (
              <div
                key={tag.id}
                className={cn(
                  'p-4 rounded-lg border-2 transition-all',
                  tag.is_active
                    ? darkMode
                      ? 'bg-gray-900 border-gray-700 hover:border-gray-600'
                      : 'bg-white border-gray-200 hover:border-gray-300'
                    : darkMode
                    ? 'bg-gray-800 border-gray-700 opacity-75'
                    : 'bg-gray-50 border-gray-200 opacity-75'
                )}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
                      tag.is_active ? 'bg-orange-100' : darkMode ? 'bg-gray-700' : 'bg-gray-200'
                    )}>
                      <Tag className={cn(
                        'w-5 h-5',
                        tag.is_active ? 'text-orange-600' : darkMode ? 'text-gray-400' : 'text-gray-500'
                      )} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className={cn('text-base font-bold font-mono', darkMode ? 'text-gray-100' : 'text-gray-900')}>
                          {tag.tag_value}
                        </h4>
                        {tag.tag_type && (
                          <span className={cn(
                            'px-2 py-0.5 rounded-full text-xs font-medium',
                            darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                          )}>
                            {tagTypeLabel(tag.tag_type)}
                          </span>
                        )}
                      </div>
                      {tag.label && (
                        <p className={cn('text-sm', darkMode ? 'text-gray-400' : 'text-gray-600')}>
                          {tag.label}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      tag.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {tag.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                    {canManage && (
                      <>
                        <button
                          onClick={() => setEditingTag(tag)}
                          className={cn(
                            'p-1.5 rounded hover:bg-gray-700 transition-colors',
                            darkMode ? 'text-gray-400 hover:text-blue-400' : 'text-gray-500 hover:text-blue-600'
                          )}
                          title="Editar tag"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(tag)}
                          className={cn(
                            'p-1.5 rounded hover:bg-gray-700 transition-colors',
                            darkMode ? 'text-gray-400 hover:text-red-400' : 'text-gray-500 hover:text-red-600'
                          )}
                          title="Excluir tag"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {(tag.notes || tag.tag_value_dec) && (
                  <div className="mt-3 pt-3 border-t border-gray-700/30 space-y-1">
                    {tag.tag_value_dec !== undefined && (
                      <p className={cn('text-xs', darkMode ? 'text-gray-500' : 'text-gray-400')}>
                        Decimal: {tag.tag_value_dec}
                      </p>
                    )}
                    {tag.notes && (
                      <p className={cn('text-sm', darkMode ? 'text-gray-400' : 'text-gray-600')}>
                        {tag.notes}
                      </p>
                    )}
                  </div>
                )}

                <div className="mt-2 flex items-center flex-wrap gap-x-3 gap-y-1">
                  <div className="flex items-center gap-1">
                    <Calendar className={cn('w-3 h-3', darkMode ? 'text-gray-500' : 'text-gray-400')} />
                    <p className={cn('text-xs', darkMode ? 'text-gray-500' : 'text-gray-400')}>
                      {new Date(tag.created_at).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })}
                    </p>
                  </div>
                  {tag.created_by_name && (
                    <div className="flex items-center gap-1">
                      <UserCheck className={cn('w-3 h-3', darkMode ? 'text-gray-500' : 'text-gray-400')} />
                      <p className={cn('text-xs', darkMode ? 'text-gray-500' : 'text-gray-400')}>
                        {tag.created_by_name}
                      </p>
                    </div>
                  )}
                  {tag.target_devices && tag.target_devices.length > 0 && (
                    <div className="flex items-center gap-1">
                      <Radio className={cn('w-3 h-3', darkMode ? 'text-gray-500' : 'text-gray-400')} />
                      <p className={cn('text-xs', darkMode ? 'text-gray-500' : 'text-gray-400')}>
                        {tag.target_devices.map(d => d.name).join(', ')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add/Edit Tag Modal */}
      {showAddModal && customerId && (
        <TagFormModal
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchTags();
          }}
          customerId={customerId}
          customerType={customerType}
          preselectedPerson={{
            id: person.id,
            first_name: person.first_name,
            last_name: person.last_name,
            email: person.email,
            phone: person.phone,
            profile_picture_url: person.profile_picture_url,
          }}
          preselectedOwnerType={ownerType}
          darkMode={darkMode}
        />
      )}

      {editingTag && customerId && (
        <TagFormModal
          open={!!editingTag}
          onClose={() => setEditingTag(null)}
          onSuccess={() => {
            setEditingTag(null);
            fetchTags();
          }}
          customerId={customerId}
          customerType={customerType}
          editingTag={editingTag}
          preselectedPerson={{
            id: person.id,
            first_name: person.first_name,
            last_name: person.last_name,
            email: person.email,
            phone: person.phone,
            profile_picture_url: person.profile_picture_url,
          }}
          preselectedOwnerType={ownerType}
          darkMode={darkMode}
        />
      )}
    </>
  );
};
